import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAESIDE_LOGO_URL } from "@/constants/brand";
import { toast } from "sonner";
import { ArrowLeft, Check, Sparkles, Loader2, ExternalLink } from "lucide-react";

// Inline PayPal-brand SVG (blue "PayPal" wordmark). Kept small & self-contained
// to avoid pulling in a heavy icon pack.
const PayPalIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#00457C" d="M20.3 6.2c.2 1-.1 2.9-1.1 4-1.4 1.7-3.5 2.5-6 2.5h-1c-.3 0-.6.2-.7.5l-.9 4.7-.3 1.5c-.1.3-.4.6-.7.6H7.2c-.3 0-.5-.3-.5-.6l1.8-9.5c.1-.4.4-.7.8-.7h4.8c2.5 0 4.6.4 5.4 1.9.1.1.1.3.1.4.2.3.6.5.7.7z"/>
    <path fill="#0079C1" d="M9.2 2h4.9c1.4 0 2.8.1 3.9.6 1 .4 1.8 1.1 2.1 2.1.4 1.1.2 2.5-.7 4.1-1.4 2.4-3.5 3-6.1 3.1h-1c-.3 0-.6.2-.7.5l-.9 4.7-.3 1.5c-.1.3-.4.6-.7.6H7.2c-.3 0-.5-.3-.5-.6L8.5 2.6c.1-.3.4-.6.7-.6z"/>
  </svg>
);

const tierRank = { free: 0, trade: 1, business: 2, lifetime: 3 };

const Billing = () => {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [paypal, setPaypal] = useState({ configured: false, mode: "sandbox" });

  const load = async () => {
    try {
      const [p, m, pp] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/me"),
        api.get("/paypal/status"),
      ]);
      setPlans(p.data.plans);
      setMe(m.data);
      setPaypal(pp.data);
    } catch {
      toast.error("Could not load billing");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // If we arrived here because the trial expired, surface a clear message
  // and clear the flag so subsequent refreshes don't repeat the toast.
  useEffect(() => {
    if (sp.get("trial_expired") === "1") {
      toast.warning("Your Field Plan trial has ended — subscribe to keep editing.");
      const next = new URLSearchParams(sp);
      next.delete("trial_expired");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll status after redirect from Stripe or PayPal
  useEffect(() => {
    const sessionId = sp.get("session_id");
    const success = sp.get("success");
    const canceled = sp.get("canceled");
    const paypalFlag = sp.get("paypal");
    if (canceled || paypalFlag === "cancel") {
      toast.info("Checkout canceled");
      setSp({}, { replace: true });
      return;
    }
    // PayPal return: capture the approved subscription / order
    if (paypalFlag === "1") {
      const token = sp.get("token") || sp.get("subscription_id") || sp.get("ba_token");
      if (!token) { setSp({}, { replace: true }); return; }
      (async () => {
        try {
          await api.get(`/paypal/capture?token=${encodeURIComponent(token)}`);
          toast.success("Payment received — plan activated");
          setSp({}, { replace: true });
          load();
          refreshUser();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "PayPal capture failed");
          setSp({}, { replace: true });
        }
      })();
      return;
    }
    if (!success || !sessionId) return;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          clearInterval(iv);
          toast.success("Payment received — plan activated");
          setSp({}, { replace: true });
          load();
          refreshUser();
        } else if (attempts >= 30) {
          clearInterval(iv);
          toast.warning("Still processing — refresh in a moment");
        }
      } catch {
        if (attempts >= 30) clearInterval(iv);
      }
    }, 2000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const startCheckout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const r = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const startPaypal = async (lookup_key) => {
    setBusy(`paypal-${lookup_key}`);
    try {
      const r = await api.post("/paypal/start", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.approval_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start PayPal");
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
      setBusy("");
    }
  };

  const currentTier = me?.tier || "free";
  const currentRank = tierRank[currentTier] ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-4 md:px-8 py-4 border-b-2 border-foreground bg-background sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button data-testid="back-to-jobs" onClick={() => nav("/jobs")}
            className="p-2 border-2 border-foreground hover:bg-foreground hover:text-background transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <img src={BRAESIDE_LOGO_URL} alt="Braeside Plumbing and Drainage" className="h-10 md:h-12 w-auto" />
            <div className="hidden sm:block h-8 w-px bg-foreground/40" />
            <span className="font-heading font-black text-lg md:text-xl uppercase tracking-tight">Field Plan</span>
          </Link>
        </div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          {user?.email}
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Billing</div>
            <h1 className="font-heading font-black uppercase text-4xl md:text-5xl mt-1">Pick your plan</h1>
            <p className="mt-2 text-muted-foreground max-w-xl">
              Prices in AUD. Powered by Stripe. Cancel any time from the billing portal.
            </p>
          </div>
          {me && (
            <div data-testid="current-plan"
              className="border-2 border-foreground bg-background px-4 py-3 shadow-block-sm">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Current plan</div>
              <div className="font-heading font-black uppercase text-xl mt-0.5">
                {currentTier === "free" ? (me.status === "trialing" ? "Free trial" : "No plan yet") : currentTier}
              </div>
              {me.status === "trialing" && user?.trial_ends_at && (
                <div className="font-mono text-[10px] uppercase mt-1 text-[#2563EB] font-bold">
                  Trial ends {new Date(user.trial_ends_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}
                  {" · "}
                  {Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000))} days left
                </div>
              )}
              {me.status && me.status !== "inactive" && me.status !== "trialing" && (
                <div className="font-mono text-[10px] uppercase mt-1">Status: {me.status}</div>
              )}
              {me.stripe_customer_id && (
                <button data-testid="open-portal" onClick={openPortal} disabled={busy === "portal"}
                  className="btn-ghost-rugged text-[11px] mt-2 inline-flex items-center gap-2 py-1.5">
                  {busy === "portal" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
                  Manage billing
                </button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="font-mono text-sm">Loading plans…</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map(p => {
              // "Current" only applies when subscription is genuinely active
              // (not an expired trial with a leftover subscription_tier).
              const statusActive = me?.status === "active" || currentTier === "lifetime";
              const isCurrent = statusActive && currentTier === p.tier;
              const isDowngrade = tierRank[p.tier] < currentRank && currentRank > 0;
              return (
                <div key={p.lookup_key} data-testid={`plan-${p.tier}`}
                  className={`relative border-2 border-foreground bg-background p-6 flex flex-col ${p.popular ? "shadow-block" : "shadow-block-sm"}`}>
                  {p.popular && (
                    <div className="absolute -top-3 left-4 bg-foreground text-background font-heading font-black uppercase text-[10px] tracking-widest px-2 py-1 border-2 border-foreground inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Most popular
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute -top-3 right-4 bg-[#2563EB] text-white font-heading font-black uppercase text-[10px] tracking-widest px-2 py-1 border-2 border-foreground">
                      Current
                    </div>
                  )}
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.tagline}</div>
                  <div className="font-heading font-black uppercase text-3xl mt-1">{p.name}</div>
                  <div className="mt-4">
                    <span className="font-heading font-black text-5xl">{p.price_display}</span>
                    <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground ml-2">{p.cadence}</span>
                  </div>
                  <ul className="mt-6 space-y-2 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`choose-${p.tier}`}
                    disabled={busy === p.lookup_key || isCurrent}
                    onClick={() => startCheckout(p.lookup_key)}
                    className={`mt-6 ${isCurrent ? "btn-ghost-rugged" : "btn-rugged"} w-full inline-flex items-center justify-center gap-2`}
                  >
                    {busy === p.lookup_key ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                    ) : isCurrent ? (
                      "You're on this plan"
                    ) : isDowngrade ? (
                      "Switch to this plan"
                    ) : p.mode === "payment" ? (
                      "Pay with card"
                    ) : (
                      `Card — ${p.name}`
                    )}
                  </button>
                  {!isCurrent && paypal.configured && (
                    <button
                      data-testid={`choose-paypal-${p.tier}`}
                      disabled={busy === `paypal-${p.lookup_key}`}
                      onClick={() => startPaypal(p.lookup_key)}
                      className="mt-2 btn-ghost-rugged w-full inline-flex items-center justify-center gap-2"
                    >
                      {busy === `paypal-${p.lookup_key}` ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                      ) : (
                        <><PayPalIcon className="w-4 h-4" /> Pay with PayPal</>
                      )}
                    </button>
                  )}
                  {!isCurrent && !paypal.configured && (
                    <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground text-center">
                      PayPal coming soon
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 border-2 border-foreground bg-background p-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Test cards work in sandbox mode. Use <span className="text-foreground">4242 4242 4242 4242</span> · any future expiry · any CVC. Real cards do not get charged until the account is claimed.
        </div>
      </main>
    </div>
  );
};

export default Billing;
